import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiUpload, apiGet, apiDelete } from "../lib/api";
import { COLORS } from "../lib/colors";
import BulkUpload from "./BulkUpload";

export default function FacultyUpload({ institutionId }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const [tab, setTab] = useState("list");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [faculty, setFaculty] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const loadFaculty = async () => {
    if (!institutionId || !token) return;
    setListLoading(true);
    try {
      const data = await apiGet(`/faculty/${institutionId}/list`, token);
      setFaculty(data.faculty || []);
    } catch {
      setFaculty([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadFaculty(); }, [institutionId, token]);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete ${name}?`)) return;
    try {
      await apiDelete(`/faculty/cv/${id}`, token);
      setFaculty((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleSingle = async () => {
    if (!file || !institutionId) return;
    setLoading(true);
    setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await apiUpload(`/upload/faculty/${institutionId}`, formData, token);
      setResult(data);
      loadFaculty();
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (!institutionId) {
    return <EmptyState message="Select an institution in Setup first." />;
  }

  const tabs = [
    { key: "list", label: `CVs (${faculty.length})` },
    { key: "bulk", label: "Bulk Upload" },
    { key: "single", label: "Single Upload" },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Faculty CVs</h2>
      <p style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
        Upload and manage faculty CVs. Analyzes qualifications, research output, international experience, and industry engagement.
      </p>

      <TabBar tabs={tabs} tab={tab} setTab={setTab} />

      {tab === "list" && (
        <FacultyTable faculty={faculty} loading={listLoading} onDelete={handleDelete} onRefresh={loadFaculty} />
      )}

      {tab === "bulk" && (
        <BulkUpload uploadPath="faculty" entityId={institutionId} onComplete={loadFaculty} />
      )}

      {tab === "single" && (
        <div style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 24,
        }}>
          <input type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files[0])} style={{ marginBottom: 16 }} />
          <br />
          <button
            onClick={handleSingle}
            disabled={!file || loading}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              backgroundColor: file && !loading ? COLORS.accent : COLORS.border,
              color: file && !loading ? "#fff" : COLORS.textMuted,
              border: "none", borderRadius: 8, cursor: file && !loading ? "pointer" : "not-allowed",
            }}
          >{loading ? "Processing..." : "Process CV"}</button>

          {result && !result.error && (
            <ResultCard
              score={result.efmd_score}
              recommended={result.recommended}
              strengths={result.strengths}
              risks={result.risks}
            />
          )}
          {result?.error && (
            <div style={{ marginTop: 16, backgroundColor: COLORS.redLight, color: COLORS.red, padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>
              {result.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FacultyTable({ faculty, loading, onDelete, onRefresh }) {
  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 14 }}>Loading faculty CVs...</div>;
  }

  if (faculty.length === 0) {
    return (
      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, padding: 40, textAlign: "center",
      }}>
        <p style={{ fontSize: 14, color: COLORS.textMuted }}>No faculty CVs uploaded yet. Use Bulk Upload or Single Upload to add CVs.</p>
      </div>
    );
  }

  const recommended = faculty.filter((f) => f.include_recommended);
  const avgScore = Math.round(faculty.reduce((sum, f) => sum + (f.efmd_score || 0), 0) / faculty.length);

  return (
    <div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <StatCard label="Total CVs" value={faculty.length} />
        <StatCard label="Recommended" value={recommended.length} color={COLORS.green} />
        <StatCard label="Avg Score" value={avgScore} />
        <div style={{ flex: 1 }} />
        <button onClick={onRefresh} style={{
          padding: "6px 14px", fontSize: 12, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, backgroundColor: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer",
        }}>Refresh</button>
      </div>

      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, overflow: "hidden",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: COLORS.surfaceAlt, borderBottom: `1px solid ${COLORS.border}` }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Title</th>
              <th style={thStyle}>Department</th>
              <th style={thStyle}>Degree</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Score</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Include</th>
              <th style={{ ...thStyle, width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {faculty.map((f) => (
              <FacultyRow key={f.id} f={f} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CvDownloadBtn({ cvType, cvId, name }) {
  const { session } = useAuth();
  const handleDownload = async (e) => {
    e.stopPropagation();
    const API_URL = import.meta.env.VITE_API_URL || "https://api-calibre.edtechsolutions.io";
    try {
      const res = await fetch(`${API_URL}/cv/${cvType}/${cvId}/download`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/\s+/g, '_')}_CV.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert("CV download failed: " + err.message); }
  };
  return (
    <button onClick={handleDownload} style={{
      padding: "4px 12px", fontSize: 11, border: `1px solid ${COLORS.accent || COLORS.blue || '#58a6ff'}`,
      borderRadius: 4, background: "none", color: COLORS.accent || COLORS.blue || '#58a6ff', cursor: "pointer",
    }}>↓ CV</button>
  );
}

function FacultyRow({ f, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const hasPhd = f.highest_degree === "PhD" || f.highest_degree === "Doctoral";
  const pubs = f.peer_reviewed_count_5yr || 0;
  const intlYrs = f.international_experience_years || 0;
  const industryYrs = f.industry_experience_years || 0;
  const consulting = f.consulting_active;
  const ersR = f.ers_research;
  const ersT = f.ers_teaching;

  const criteria = [
    { code: "1.3.1", name: "Qualifications", value: hasPhd ? `PhD ${f.degree_field || ""}`.trim() : (f.highest_degree || "—"), ok: hasPhd },
    { code: "1.3.2", name: "Research", value: pubs > 0 ? `${pubs} publications` : "No data", ok: pubs > 0 },
    { code: "1.3.3", name: "Teaching", value: "No data", ok: null },
    { code: "1.3.4", name: "International", value: intlYrs > 0 ? `${intlYrs}yr` : "No data", ok: intlYrs > 0 },
    { code: "1.3.5", name: "Practice", value: consulting ? "Consulting" : industryYrs > 0 ? `${industryYrs}yr industry` : "No data", ok: consulting || industryYrs > 0 },
    { code: "1.3.6", name: "ERS", value: ersR ? "Research" : ersT ? "Teaching" : "No data", ok: ersR || ersT },
  ];

  return (
    <>
      <tr
        onClick={() => setExpanded(!expanded)}
        style={{ borderBottom: `1px solid ${COLORS.borderLight}`, cursor: "pointer" }}
      >
        <td style={tdStyle}><span style={{ fontWeight: 500 }}>{f.full_name}</span></td>
        <td style={tdStyle}>{f.current_title || "—"}</td>
        <td style={tdStyle}>{f.department || "—"}</td>
        <td style={tdStyle}>{f.highest_degree || "—"}</td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          <ScoreBadge score={f.efmd_score} />
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          {f.include_recommended ? (
            <span style={{ color: COLORS.green, fontWeight: 600 }}>Yes</span>
          ) : (
            <span style={{ color: COLORS.orange }}>Review</span>
          )}
        </td>
        <td style={tdStyle}>
          <span style={{ color: COLORS.textMuted, fontSize: 10 }}>{expanded ? "▲" : "▼"}</span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ padding: 0 }}>
            <div style={{
              background: COLORS.surfaceAlt, padding: "8px 16px",
              borderBottom: `1px solid ${COLORS.border}`,
            }}>
              {criteria.map((c, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", padding: "6px 8px",
                  borderBottom: i < criteria.length - 1 ? `1px solid ${COLORS.borderLight}` : "none",
                  fontSize: 13,
                }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: "50%", display: "flex",
                    alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700,
                    background: c.ok === true ? COLORS.green : c.ok === false ? COLORS.orange : COLORS.border,
                    color: c.ok != null ? "#fff" : COLORS.textMuted, flexShrink: 0,
                  }}>{c.ok === true ? "✓" : c.ok === false ? "!" : "—"}</span>
                  <span style={{ width: 50, color: COLORS.textMuted, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, marginLeft: 8 }}>{c.code}</span>
                  <span style={{ width: 120, color: COLORS.textSecondary }}>{c.name}</span>
                  <span style={{ fontWeight: 600, color: COLORS.text }}>{c.value}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 6 }}>
                <CvDownloadBtn cvType="faculty" cvId={f.id} name={f.full_name} />
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(f.id, f.full_name); }}
                  style={{
                    padding: "4px 12px", fontSize: 11, border: `1px solid ${COLORS.red}`,
                    borderRadius: 4, background: "none", color: COLORS.red, cursor: "pointer",
                  }}
                >Delete</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function TabBar({ tabs, tab, setTab }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          style={{
            padding: "8px 20px", fontSize: 13, fontWeight: 500, borderRadius: 8,
            border: `1px solid ${tab === t.key ? COLORS.accent : COLORS.border}`,
            backgroundColor: tab === t.key ? COLORS.accentLight : COLORS.surface,
            color: tab === t.key ? COLORS.accent : COLORS.textSecondary,
            cursor: "pointer",
          }}
        >{t.label}</button>
      ))}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: 8, padding: "10px 16px", minWidth: 80,
    }}>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || COLORS.text }}>{value}</div>
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 65 ? COLORS.green : score >= 45 ? COLORS.orange : COLORS.red;
  const bg = score >= 65 ? COLORS.greenLight : score >= 45 ? COLORS.orangeLight : COLORS.redLight;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 12, fontWeight: 600, color, backgroundColor: bg,
    }}>{score}</span>
  );
}

function ResultCard({ score, recommended, strengths, risks }) {
  return (
    <div style={{
      marginTop: 20, padding: 20, borderRadius: 12,
      border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surfaceAlt,
    }}>
      <div style={{ display: "flex", gap: 24, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>Score</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: score >= 45 ? COLORS.green : COLORS.orange }}>{score}/100</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>Recommendation</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: recommended ? COLORS.green : COLORS.orange, marginTop: 4 }}>
            {recommended ? "Include" : "Review"}
          </div>
        </div>
      </div>
      {strengths?.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>Strengths</div>
          {strengths.map((s, i) => <div key={i} style={{ fontSize: 13, color: COLORS.green }}>+ {s}</div>)}
        </div>
      )}
      {risks?.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>Risks</div>
          {risks.map((r, i) => <div key={i} style={{ fontSize: 13, color: COLORS.orange }}>- {r}</div>)}
        </div>
      )}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface, borderRadius: 12,
      border: `1px solid ${COLORS.border}`, padding: 40, textAlign: "center",
    }}>
      <p style={{ fontSize: 14, color: COLORS.textMuted }}>{message}</p>
    </div>
  );
}

const thStyle = {
  textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600,
  color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px",
};

const tdStyle = {
  padding: "10px 12px", color: COLORS.text,
};
